import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { ReactNode } from 'react';
import type { LibraryEntry } from '@bw/document';
import { versionLabel } from '../build.js';
import type { Command } from '../commands.js';

/** A focus-trapping modal. Escape closes; focus returns to the opener. */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>(
      'input, button, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || ref.current === null) return;
      // Selects and textareas are focusable too: leaving them out of this list
      // means the wrap-around is computed from the wrong first and last
      // elements, and Tab escapes the dialog whenever a select sits at an end.
      const focusable = [
        ...ref.current.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (focusable.length === 0) return;
      const firstEl = focusable[0] as HTMLElement;
      const lastEl = focusable[focusable.length - 1] as HTMLElement;
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="scrim" onPointerDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <header>{title}</header>
        <div className="content">{children}</div>
        <footer>{footer ?? <button className="btn cta" onClick={onClose}>Close</button>}</footer>
      </div>
    </div>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  extraLabel,
  onExtra,
}: {
  readonly title: string;
  readonly message: ReactNode;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly extraLabel?: string;
  readonly onExtra?: () => void;
}): JSX.Element {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          {extraLabel !== undefined && onExtra !== undefined && (
            <button className="btn" onClick={onExtra}>
              {extraLabel}
            </button>
          )}
          <button className="btn cta" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      {message}
    </Modal>
  );
}

/** Ctrl+K — every command, fuzzy-searchable, with its shortcut (design C6). */
export function CommandPalette({
  commands,
  onClose,
  onRun,
}: {
  readonly commands: readonly Command[];
  readonly onClose: () => void;
  readonly onRun: (id: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);

  const matches = useMemo(() => {
    const seen = new Set<string>();
    const unique = commands.filter((command) => {
      const key = `${command.group}:${command.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const needle = query.trim().toLowerCase();
    if (needle === '') return unique.slice(0, 40);
    return unique
      .filter((command) => `${command.group} ${command.label}`.toLowerCase().includes(needle))
      .slice(0, 40);
  }, [commands, query]);

  useEffect(() => setIndex(0), [query]);

  return (
    <Modal title="Commands" onClose={onClose} footer={<span className="note">Enter runs · Esc closes</span>}>
      <div className="palette">
        <input
          data-text-input
          autoFocus
          value={query}
          placeholder="Search commands"
          aria-label="Search commands"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setIndex((current) => Math.min(current + 1, matches.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setIndex((current) => Math.max(current - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const chosen = matches[index];
              if (chosen !== undefined) {
                onClose();
                onRun(chosen.id);
              }
            }
          }}
        />
        <ul role="listbox">
          {matches.map((command, position) => {
            const disabled = command.enabled !== undefined && !command.enabled();
            return (
              <li
                key={command.id}
                role="option"
                aria-selected={position === index}
                style={disabled ? { opacity: 0.4 } : undefined}
                onPointerEnter={() => setIndex(position)}
                onClick={() => {
                  if (disabled) return;
                  onClose();
                  onRun(command.id);
                }}
              >
                <span className="group">{command.group}</span>
                <span>{command.label}</span>
                {command.shortcut !== undefined && <span className="keys">{command.shortcut}</span>}
              </li>
            );
          })}
          {matches.length === 0 && <li aria-disabled>No matching command</li>}
        </ul>
      </div>
    </Modal>
  );
}

export function LibraryDialog({
  entries,
  onClose,
  onOpen,
  onDelete,
}: {
  readonly entries: readonly LibraryEntry[];
  readonly onClose: () => void;
  readonly onOpen: (id: string) => void;
  readonly onDelete: (id: string) => void;
}): JSX.Element {
  return (
    <Modal title="Recent projects" onClose={onClose}>
      {entries.length === 0 ? (
        <p className="note">
          Nothing saved on this device yet. Projects you save appear here, stored in the browser
          alongside the file you choose.
        </p>
      ) : (
        <ul className="library">
          {entries.map((entry) => (
            <li key={entry.id}>
              <button className="btn" onClick={() => onOpen(entry.id)}>
                {entry.name}
              </button>
              <span className="meta">
                {entry.brickCount} bricks · {new Date(entry.savedAt).toLocaleString()}
              </span>
              <button className="btn" aria-label={`Delete ${entry.name}`} onClick={() => onDelete(entry.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/** Clause 16.4 — the controls help, in the specified order. */
export function HelpDialog({ onClose }: { readonly onClose: () => void }): JSX.Element {
  return (
    <Modal
      title="Controls"
      onClose={onClose}
      footer={
        <>
          {/* Which build this is. The app updates itself on a refresh, so the
              only remaining question is which one you are on — and this is
              where it can be read out over a telephone. */}
          <span className="note build">{versionLabel()}</span>
          <button className="btn cta" onClick={onClose}>Close</button>
        </>
      }
    >
      <h3>The order of the work</h3>
      <p>
        A building is made in three steps, named along the top of the window.{' '}
        <b>Draw walls</b> is where each wall&rsquo;s elevation is laid, one wall at a time.{' '}
        <b>Arrange plan</b> looks down on those walls and positions them on the ground.{' '}
        <b>View in 3D</b> shows the result. Step back and forth freely; each step reports what it
        has, so the plan says whether the outline closes and 3D waits until there are bricks.
      </p>
      <h3>Walls</h3>
      <p>
        Every wall in the project is listed in the inspector, in every step. Click one to work on
        it — that is the wall you draw and the wall the plan positions, which are the same wall.{' '}
        <b>Add wall</b> starts a new one at the end of the current one, turned a quarter turn, so
        walls added in order walk round the outline: four of equal length close it exactly. Rename
        a wall by double-clicking it, and step through them with [ and ].
      </p>
      <h3>Sketch</h3>
      <p>
        Press K or choose Sketch. Click to place points; click the first point again, or press
        Enter, to close the run into a shape. Esc ends a run and keeps its lines as open guides.
        Choose <b>wall lines</b> for geometry that is part of the wall and can be filled, or{' '}
        <b>construction lines</b> for guides — drawn faintly, never filled, and snapped to just the
        same. Mark any closed shape as wall, window, door or flint. A wall shape can be filled with
        a bond in one action, leaving every window, door and flint panel out, or you can lay it by
        hand: bricks snap to every line either way.
      </p>
      <h3>Sketch snapping</h3>
      <p>
        A point being placed catches whatever is nearest: a drawn corner, the middle of a drawn
        line, anywhere along a drawn line, or a corner or edge of laid brickwork. It also holds
        level or plumb with the point before it, which is what squares a drawing up. The drawing
        marks what was caught and names it, so a point that has locked onto a brick corner cannot
        be mistaken for one that merely passes near it. Hold <b>Alt</b> to place a point exactly
        where the pointer is and catch nothing.
      </p>
      <h3>Editing a sketch</h3>
      <p>
        With no run in progress the sketch tool edits what is already drawn. Click a line to
        select the shape it belongs to; drag a line to move that whole shape, or that whole loose
        line. Drag a corner to move only that corner — everything a moved corner or shape passes
        snaps just as a new point does. Double-click a line to add a corner to it, which splits a
        loose line in two. Delete removes whatever is selected. A click on a corner still starts a
        new run there, so an outline can be continued from a point that already exists.
      </p>
      <h3>Build</h3>
      <p>
        Choose Stretcher, Header, Quarter bat, Three-quarter bat or Block, then click to lay. The
        same unit stays active. Move near a brick edge to snap to the wall&rsquo;s perpend gap or the
        fixed 10 mm bed joint. Stretcher ends snap either side of a supporting brick&rsquo;s centre for
        coordinated half bonds. A unit&rsquo;s left edge, centre or right edge can snap to the Y-axis.
        Hold Alt while positioning to bypass snapping. Where an outline has been sketched, a
        brick edge also snaps to any drawn line and seats into its corners.
      </p>
      <h3>Wall joints</h3>
      <p>
        Enter a perpend gap in the inspector and choose Apply. Values round to the nearest 0.125 mm.
        The complete wall re-coordinates as one undoable change; the bed joint between courses
        remains fixed at 10 mm.
      </p>
      <h3>Rotate</h3>
      <p>Press R to turn the preview through 90°. This supports soldiers and lintel patterns.</p>
      <h3>Paint</h3>
      <p>
        Choose a material, then click any laid bricks. Drag to paint a run. Paint remains active for
        repeated colouring.
      </p>
      <h3>Select and copy</h3>
      <p>
        Press Esc for Select. Drag around multiple bricks, Shift-click to add, or Ctrl-click to
        toggle. Ctrl+A selects all. Ctrl+C copies the pattern; Ctrl+V makes it follow the pointer.
        Click repeatedly to paste it, and press Esc when finished.
      </p>
      <h3>Measure</h3>
      <p>
        Press M or choose Measure. Click a first brick corner and then a second. The tool uses the
        closest brick and snaps to its nearest corner, showing X and Y distances, the absolute
        distance rounded to the nearest millimetre, and the unsigned angle from horizontal to the
        nearest 0.1° (0° horizontal, 90° vertical). Click again to start another measurement.
      </p>
      <h3>Navigate</h3>
      <p>
        Press P or choose Pan, then drag with the left mouse button. You can also drag with the
        middle or right button, or hold Space and drag, for temporary panning. Use the mouse wheel
        to zoom and press F to fit the whole wall.
      </p>
      <h3>Edit</h3>
      <p>
        Delete removes every selected brick as one action. Ctrl+Z and Ctrl+Y undo and redo. Ctrl+K
        opens the command palette. All positions and dimensions are stored in millimetres.
      </p>
    </Modal>
  );
}
